export function findLocationTypeId(locationTypes, ...keywords) {
  const lt = locationTypes.find((t) => {
    const name = (t.location_type || "").toLowerCase();
    return keywords.some((kw) => name.includes(kw.toLowerCase()));
  });
  return lt ? lt.location_type_id : null;
}

/**
 * Returns the valid parents for a location type in the configured hierarchy:
 * Country → State/Province → City/District → Postal Code.
 * Unknown/custom location types retain the previous unrestricted behaviour.
 */
export function filterParentLocations(locations, locationTypes, locationTypeId) {
  if (!locationTypeId) return [];

  const selectedType = locationTypes.find(
    (type) => String(type.location_type_id) === String(locationTypeId)
  );
  const typeName = (selectedType?.location_type || "").toLowerCase();

  let parentKeywords;
  if (typeName.includes("postal")) {
    parentKeywords = ["city", "district"];
  } else if (typeName.includes("city") || typeName.includes("district")) {
    parentKeywords = ["state", "province"];
  } else if (typeName.includes("state") || typeName.includes("province")) {
    parentKeywords = ["country"];
  } else if (typeName.includes("country")) {
    return [];
  } else {
    return locations;
  }

  const parentTypeIds = new Set(
    locationTypes
      .filter((type) => {
        const name = (type.location_type || "").toLowerCase();
        return parentKeywords.some((keyword) => name.includes(keyword));
      })
      .map((type) => String(type.location_type_id))
  );

  return locations.filter((location) =>
    parentTypeIds.has(String(location.location_type_id))
  );
}

export function filterCountries(locations, locationTypes) {
  const countryTypeId = findLocationTypeId(locationTypes, "country");
  if (!countryTypeId) return [];
  return locations.filter((l) => String(l.location_type_id) === String(countryTypeId));
}

/** True when locationId is the ancestor or a descendant under ancestorId. */
export function isLocationUnderParent(locations, locationId, ancestorId) {
  if (!locationId || !ancestorId) return false;

  let current = locations.find((l) => String(l.location_id) === String(locationId));
  while (current) {
    if (String(current.location_id) === String(ancestorId)) return true;
    if (!current.parent_location_id) return false;
    current = locations.find(
      (l) => String(l.location_id) === String(current.parent_location_id),
    );
  }

  return false;
}

export function filterCitiesForParent(locations, locationTypes, parentLocationId) {
  const cityTypeId = findLocationTypeId(locationTypes, 'city', 'district');
  if (!cityTypeId || !parentLocationId) return [];

  return locations.filter(
    (l) =>
      String(l.location_type_id) === String(cityTypeId)
      && isLocationUnderParent(locations, l.location_id, parentLocationId),
  );
}

/** Direct children of a parent location (e.g. India → Delhi NCR). */
export function filterChildLocations(locations, parentLocationId) {
  if (!parentLocationId) return [];
  return locations.filter(
    (l) => String(l.parent_location_id) === String(parentLocationId)
  );
}

/** Walk parent chain and inherit country-level fields from ancestors. */
export function resolveCountryFieldsFromParent(locations, parentLocationId) {
  const result = {
    country_code: "",
    country_flag_image_path: "",
    treatment_cost_currency: "",
    local_currency: "",
  };
  if (!parentLocationId) return result;

  let current = locations.find((l) => String(l.location_id) === String(parentLocationId));
  const seen = new Set();

  while (current && !seen.has(String(current.location_id))) {
    seen.add(String(current.location_id));
    if (!result.country_code && current.country_code) result.country_code = current.country_code;
    if (!result.country_flag_image_path && current.country_flag_image_path) {
      result.country_flag_image_path = current.country_flag_image_path;
    }
    if (!result.treatment_cost_currency && current.treatment_cost_currency) {
      result.treatment_cost_currency = current.treatment_cost_currency;
    }
    if (!result.local_currency && current.local_currency) {
      result.local_currency = current.local_currency;
    }
    if (
      result.country_code
      && result.country_flag_image_path
      && result.treatment_cost_currency
      && result.local_currency
    ) break;
    if (!current.parent_location_id) break;
    current = locations.find((l) => String(l.location_id) === String(current.parent_location_id));
  }

  return result;
}

/** Resolve flag image for a location, including inheritance from parent chain. */
export function resolveCountryFlagImagePath(locations, location) {
  if (location?.country_flag_image_path) return location.country_flag_image_path;
  if (!location?.parent_location_id) return "";
  return resolveCountryFieldsFromParent(locations, location.parent_location_id).country_flag_image_path;
}
